import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Loader2, ExternalLink, AlertCircle } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

export function PurchaseDialog({
  articlePrice = 0.29,
  originalUrl,
  open,
  onClose,
  onPurchase,
  loading = false,
}) {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [encodedSlug, setEncodedSlug] = useState('')

  useEffect(() => {
    if (slug) {
      setEncodedSlug(btoa(slug))
    }
  }, [slug])

  if (!open) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-slate-700 to-slate-800 p-6">
            <div className="flex items-center gap-3 text-white">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Premium Article</h2>
                <p className="text-slate-100 text-sm">Purchase required to read</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Price */}
            <div className="bg-slate-50 dark:bg-slate-900/20 border border-slate-200 dark:border-slate-800 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-700 dark:text-gray-300 font-medium">Article Price:</span>
                <span className="text-3xl font-bold text-primary dark:text-primary">
                  ${articlePrice.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Info */}
            <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <p className="flex items-start gap-2">
                <span className="text-green-600 dark:text-green-400 mt-0.5">✓</span>
                <span>One-time payment - read anytime</span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-green-600 dark:text-green-400 mt-0.5">✓</span>
                <span>Full access to article content</span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-green-600 dark:text-green-400 mt-0.5">✓</span>
                <span>Support quality journalism</span>
              </p>
            </div>

            {/* Alternative */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Don't want to purchase? You can read it on the original site:
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => window.open(originalUrl, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Read on Original Site
              </Button>
            </div>

            {/* Policy Link */}
            <div className="text-center">
              <a
                href={`/article/purchases-policy?ref=${encodedSlug}`}
                className="text-sm text-primary dark:text-primary hover:underline"
                onClick={(e) => {
                  e.preventDefault()
                  navigate(`/article/purchases-policy?ref=${encodedSlug}`)
                }}
              >
                Read Purchase Policy
              </a>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 flex gap-3">
            {!loading && (
              <Button
                variant="outline"
                className="flex-1"
                onClick={onClose}
              >
                Cancel
              </Button>
            )}
            <Button
              className="flex-1 bg-primary hover:bg-primary/90"
              onClick={onPurchase}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Purchase for ${articlePrice.toFixed(2)}
                </>
              )}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

// Alert for free users
export function FreeUserAlert() {
  return (
    <div className="bg-slate-50 dark:bg-slate-900/20 border border-slate-200 dark:border-slate-800 rounded-xl p-4 mb-6">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-primary dark:text-primary flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
            Premium Article
          </h4>
          <p className="text-sm text-slate-800 dark:text-slate-200">
            You will be charged <strong>$0.29</strong> one-time for each premium article.
          </p>
        </div>
      </div>
    </div>
  )
}
